// @ts-check
// Exibição de valores por tipo de campo (doc 09, 13.1; doc 04, 2.8).
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { Avatar } from '../../ui/avatar.js';
import { Chip, RecordChip, Tag } from '../../ui/chip.js';
import {
  FIELD_TYPE,
  findOption,
  formatFullName,
  isFieldValueEmpty,
  isManyToOneRelation,
} from '../../lib/field-metadata.js';
import {
  formatCurrencyMicros,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPhone,
  formatRelativeDate,
  formatRelativeToNow,
  getUrlHostname,
  toAbsoluteUrl,
} from '../../lib/format.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { formatPostcodePlace, isPostcodeMalformed, toCountryLabel } from '../../../../shared/address/postcode-autofill.js';
import { isPostcodeProjection, isSubFieldProjection, readSubFieldValue } from '../../lib/address-sub-fields.js';
import { readPostcodeStatus } from '../../data/postcode-statuses.js';

/**
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 */

const ACTOR_SOURCE_ICONS = {
  API: 'IconApi',
  IMPORT: 'IconUpload',
  EMAIL: 'IconMail',
  CALENDAR: 'IconCalendar',
  SYSTEM: 'IconRobot',
  WORKFLOW: 'IconSettingsAutomation',
  WEBHOOK: 'IconWebhook',
  APPLICATION: 'IconPlug',
  BOT: 'IconRobot',
};

/**
 * Link arredondado para e-mails, telefones e links (doc 09, 8.4 RoundedLink). Com a ação "Copiar" do campo
 * (settings.clickAction = COPY), o clique copia o valor em vez de abrir o link.
 * @param {{ href: string, label: string, clickAction?: string, copyValue?: string }} props
 */
function RoundedLink({ href, label, clickAction, copyValue }) {
  if (clickAction === 'COPY') {
    return html`<a
      class="rounded-link"
      href=${href}
      title=${t('fieldEditor.display.copyHint', { value: copyValue ?? label })}
      data-click-action="COPY"
      onClick=${(event) => {
        event.preventDefault();
        event.stopPropagation();
        navigator.clipboard?.writeText(copyValue ?? label).then(
          () => showSuccessSnackbar(t('fieldEditor.display.copied')),
          () => undefined,
        );
      }}
    >${label}</a>`;
  }
  return html`<a
    class="rounded-link"
    href=${href}
    target=${href.startsWith('http') ? '_blank' : undefined}
    rel="noopener noreferrer"
    title=${label}
    onClick=${(event) => event.stopPropagation()}
  >${label}</a>`;
}

/**
 * @param {{ value: number, size?: number, onSelect?: (rating: number | null) => void }} props
 */
export function RatingStars({ value, size = 16, onSelect }) {
  return html`<span class="rating" role=${onSelect ? 'slider' : undefined} aria-valuemin="1" aria-valuemax="5" aria-valuenow=${value || undefined}>
    ${[1, 2, 3, 4, 5].map(
      (rating) => html`<span
        class="rating-star"
        data-active=${rating <= value ? 'true' : undefined}
        onClick=${onSelect
          ? (event) => {
              event.stopPropagation();
              onSelect(rating === value ? null : rating);
            }
          : undefined}
      ><${Icon} name="IconStarFilled" size=${size} /></span>`,
    )}
  </span>`;
}

/**
 * @param {string | null | undefined} ratingValue
 */
export function ratingToNumber(ratingValue) {
  if (!ratingValue) return 0;
  return Number(String(ratingValue).replace('RATING_', '')) || 0;
}

/**
 * @param {{ field: FieldMetadata, record: Record<string, any>, emptyLabel?: string | null }} props
 */
export function FieldDisplay({ field, record, emptyLabel = null }) {
  const value = record?.[field.name];

  // parte do endereço (linha "CEP" da família, colunas CEP, Bairro, Cidade): mostra só ela
  if (isSubFieldProjection(field)) return renderSubField(field, record, emptyLabel);

  if (isManyToOneRelation(field)) {
    const target = objectsByNameSingular.value.get(field.relation?.targetObjectNameSingular ?? '');
    const relatedRecord = value ?? (record?.[`${field.name}Id`] ? { id: record[`${field.name}Id`] } : null);
    if (!relatedRecord) return renderEmpty(emptyLabel);
    return html`<${RecordChip} objectMetadata=${target} record=${relatedRecord} />`;
  }

  if (field.type === FIELD_TYPE.RELATION) {
    const target = objectsByNameSingular.value.get(field.relation?.targetObjectNameSingular ?? '');
    const relatedRecords = Array.isArray(value) ? value : value?.edges?.map((edge) => edge.node) ?? [];
    if (relatedRecords.length === 0) return renderEmpty(emptyLabel);
    return html`<span class="field-display-list">
      ${relatedRecords.map((relatedRecord) => html`<${RecordChip} key=${relatedRecord.id} objectMetadata=${target} record=${relatedRecord} />`)}
    </span>`;
  }

  if (isFieldValueEmpty(field, value)) return renderEmpty(emptyLabel);

  switch (field.type) {
    case FIELD_TYPE.TEXT: {
      // linhas exibidas valem na página do registro; a tabela e o kanban continuam em uma linha (doc 04, TEXT)
      const maxRows = Number(field.settings?.displayedMaxRows ?? 0);
      return maxRows > 0
        ? html`<span class="field-display-text" data-max-rows=${maxRows} style=${`--field-max-rows: ${maxRows}`}>${String(value)}</span>`
        : html`<span class="field-display-text">${String(value)}</span>`;
    }
    case FIELD_TYPE.UUID:
      return html`<span class="field-display-text field-display-mono">${String(value)}</span>`;
    case FIELD_TYPE.NUMBER:
    case FIELD_TYPE.NUMERIC:
      return html`<span class="field-display-text field-display-number">${formatNumber(value, {
        decimals: field.settings?.decimals,
        percentage: field.settings?.type === 'percentage',
      })}</span>`;
    case FIELD_TYPE.CURRENCY:
      return html`<span class="field-display-text field-display-currency">
        ${formatCurrencyMicros(value.amountMicros, value.currencyCode, { short: field.settings?.format === 'short', decimals: field.settings?.decimals })}
      </span>`;
    case FIELD_TYPE.DATE:
      return field.settings?.displayFormat === 'RELATIVE'
        ? html`<span class="field-display-text" title=${formatDate(value)}>${formatRelativeDate(value)}</span>`
        : html`<span class="field-display-text">${formatDate(value)}</span>`;
    case FIELD_TYPE.DATE_TIME:
      return field.settings?.displayFormat === 'RELATIVE'
        ? html`<span class="field-display-text" title=${formatDateTime(value)}>${formatRelativeToNow(value)}</span>`
        : html`<span class="field-display-text">${formatDateTime(value)}</span>`;
    case FIELD_TYPE.BOOLEAN:
      return html`<span class="field-display-boolean">
        <${Icon} name=${value ? 'IconCheck' : 'IconX'} size=${14} />
        <span>${value ? t('common.true') : t('common.false')}</span>
      </span>`;
    case FIELD_TYPE.RATING:
      return html`<${RatingStars} value=${ratingToNumber(value)} />`;
    case FIELD_TYPE.SELECT: {
      const option = findOption(field, value);
      return html`<${Tag} color=${option?.color} text=${translateMetadataLabel(option?.label ?? String(value))} />`;
    }
    case FIELD_TYPE.MULTI_SELECT:
      return html`<span class="field-display-list">
        ${value.map((item) => {
          const option = findOption(field, item);
          return html`<${Tag} key=${item} color=${option?.color} text=${translateMetadataLabel(option?.label ?? item)} />`;
        })}
      </span>`;
    case FIELD_TYPE.FULL_NAME:
      return html`<span class="field-display-text">${formatFullName(value)}</span>`;
    case FIELD_TYPE.EMAILS: {
      const emails = [value.primaryEmail, ...(value.additionalEmails ?? [])].filter(Boolean);
      return html`<span class="field-display-list">
        ${emails.map((email) => html`<${RoundedLink} key=${email} href=${`mailto:${email}`} label=${email} clickAction=${field.settings?.clickAction} />`)}
      </span>`;
    }
    case FIELD_TYPE.PHONES: {
      const phones = [
        value.primaryPhoneNumber ? value : null,
        ...(value.additionalPhones ?? []).map((phone) => ({
          primaryPhoneNumber: phone.number,
          primaryPhoneCallingCode: phone.callingCode,
        })),
      ].filter(Boolean);
      return html`<span class="field-display-list">
        ${phones.map((phone) => {
          const label = formatPhone(phone);
          return html`<${RoundedLink} key=${label} href=${`tel:${label.replace(/\s|-/g, '')}`} label=${label} clickAction=${field.settings?.clickAction}
            copyValue=${label.replace(/\s|-/g, '')} />`;
        })}
      </span>`;
    }
    case FIELD_TYPE.LINKS: {
      const links = [
        value.primaryLinkUrl ? { url: value.primaryLinkUrl, label: value.primaryLinkLabel } : null,
        ...(value.secondaryLinks ?? []),
      ].filter(Boolean);
      return html`<span class="field-display-list">
        ${links.map((link) => html`<${RoundedLink} key=${link.url} href=${toAbsoluteUrl(link.url)} label=${link.label || getUrlHostname(link.url)}
          clickAction=${field.settings?.clickAction} copyValue=${toAbsoluteUrl(link.url)} />`)}
      </span>`;
    }
    case FIELD_TYPE.ADDRESS: {
      const parts = [value.addressStreet1, value.addressStreet2, value.addressCity, value.addressState, value.addressPostcode, toCountryLabel(value.addressCountry)]
        .filter(Boolean)
        .join(', ');
      return html`<span class="field-display-text">${parts}</span>`;
    }
    case FIELD_TYPE.ACTOR: {
      const sourceIcon = ACTOR_SOURCE_ICONS[value.source];
      return html`<span class="field-display-actor">
        ${sourceIcon
          ? html`<${Icon} name=${sourceIcon} size=${14} />`
          : html`<${Avatar} placeholder=${value.name} seed=${value.workspaceMemberId ?? value.name} size="sm" type="rounded" />`}
        <span class="field-display-text">${value.name || t(`actorSources.${value.source}`)}</span>
      </span>`;
    }
    case FIELD_TYPE.ARRAY:
      return html`<span class="field-display-list">
        ${value.map((item) => html`<${Chip} key=${item} label=${String(item)} variant="highlighted" />`)}
      </span>`;
    case FIELD_TYPE.RICH_TEXT: {
      const text = typeof value === 'object' ? value.markdown ?? '' : String(value);
      return html`<span class="field-display-text">${text.split('\n').find((line) => line.trim()) ?? ''}</span>`;
    }
    case FIELD_TYPE.FILES:
      return html`<span class="field-display-list">
        ${(Array.isArray(value) ? value : []).map(
          (file) => html`<${Chip} key=${file.fileId ?? file.label} label=${file.label ?? t('record.untitledFile')} variant="highlighted" leftComponent=${html`<${Icon} name="IconPaperclip" size=${14} />`} />`,
        )}
      </span>`;
    case FIELD_TYPE.RAW_JSON:
      return html`<span class="field-display-text field-display-mono">${JSON.stringify(value)}</span>`;
    default:
      return html`<span class="field-display-text">${typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>`;
  }
}

/**
 * CEP com a máscara; com withPlace, o lugar ("Cambuí, Campinas/SP") em texto secundário. CEP mal formado ou que a
 * consulta não encontrou ganha o aviso (ícone e title na coluna; o motivo por extenso na linha, no lugar do lugar,
 * que seria o do CEP antigo). As outras partes, texto simples.
 * @param {FieldMetadata} field @param {Record<string, any>} record @param {string | null} emptyLabel
 */
function renderSubField(field, record, emptyLabel) {
  const text = readSubFieldValue(record, field).trim();
  if (!text) return renderEmpty(emptyLabel);
  if (!isPostcodeProjection(field)) return html`<span class="field-display-text">${text}</span>`;
  const problem = isPostcodeMalformed(text) ? 'malformed' : readPostcodeStatus(text) === 'NOT_FOUND' ? 'notFound' : null;
  const place = field.withPlace && !problem ? formatPostcodePlace(record?.[field.name]) : '';
  return html`<span class="field-display-postcode" data-tone=${problem ? 'warning' : undefined} data-problem=${problem ?? undefined}
    title=${problem ? t(`familyPostcode.${problem}`) : undefined}>
    ${problem ? html`<${Icon} name="IconAlertTriangle" size=${14} />` : null}
    <span class="field-display-text field-display-postcode-value">${text}</span>
    ${place ? html`<span class="field-display-postcode-place">${place}</span>` : null}
    ${problem && field.withPlace ? html`<span class="field-display-postcode-problem">${t(`familyPostcode.status.${problem}`)}</span>` : null}
  </span>`;
}

/**
 * @param {string | null} emptyLabel
 */
function renderEmpty(emptyLabel) {
  if (!emptyLabel) return null;
  return html`<span class="field-display-empty">${emptyLabel}</span>`;
}
